import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { PasswordField } from "../components/PasswordField";
import { useAuth } from "../auth/AuthContext";
import { changePassword } from "../lib/api";
import { homePathFor } from "../nav/navigation";

/**
 * Смена своего пароля (D-15). До этой задачи серверный маршрут `POST /api/auth/change-password`
 * существовал с SK-4, а экрана не было вовсе — то есть сменить пароль пользователь не мог
 * никак, и рабочий пароль навсегда оставался тем, который выдал администратор.
 *
 * Два режима одной страницы:
 *   • ОБЫЧНЫЙ — человек пришёл сам («Сменить пароль» в шапке): есть кнопка «Отмена»;
 *   • ПРИНУДИТЕЛЬНЫЙ (`mustChangePassword`) — пароль выдан администратором и ещё не сменён:
 *     вместо «Отмены» объяснение, почему других разделов сейчас нет. Уйти можно только сменой
 *     пароля или выходом — ровно то, что разрешает серверный гард; интерфейс его не смягчает.
 *
 * Политику пароля проверяет СЕРВЕР (`passwordPolicy.ts`) и он же формулирует отказ по-русски.
 * Здесь её не дублируем — две проверки разойдутся, и человек увидит «пароль годится» от
 * клиента и «не годится» от сервера. Единственная клиентская проверка — совпадение с полем
 * подтверждения: сервер о втором поле не знает, это чисто экранная защита от опечатки.
 */
export function ChangePasswordPage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Гард входа стоит выше по дереву маршрутов; это защита от прямого рендера без сессии.
  if (!user) return <Navigate to="/login" replace />;

  const forced = user.mustChangePassword;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Новый пароль и подтверждение не совпадают.");
      return;
    }

    setSubmitting(true);
    const result = await changePassword(currentPassword, newPassword);
    if (!result.ok) {
      setSubmitting(false);
      setError(result.message);
      return;
    }

    // Пароли в состоянии не задерживаем. Затем перечитываем сессию: сервер снял
    // `mustChangePassword`, и до перечитывания интерфейс держал бы человека здесь же.
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    await refresh();
    void navigate(homePathFor(user?.roles ?? []), { replace: true });
  }

  return (
    <div className="mx-auto max-w-[var(--w-form-narrow)]">
      <h1 className="text-[20px] font-bold text-ink">Смена пароля</h1>

      {forced ? (
        <p
          role="status"
          className="mt-3 rounded-[var(--radius-m)] bg-soon-bg px-3 py-2 text-[13px] text-soon-fg"
        >
          Пароль вам выдал администратор, и он его знает. Задайте свой — до этого остальные разделы
          недоступны.
        </p>
      ) : (
        <p className="mt-3 text-[13px] text-muted">
          После смены пароля другие ваши сеансы будут завершены — на других устройствах потребуется
          войти заново.
        </p>
      )}

      <form
        onSubmit={onSubmit}
        className="mt-4 rounded-[var(--radius-l)] border border-border bg-surface p-6"
        aria-labelledby="change-password-heading"
      >
        <h2 id="change-password-heading" className="sr-only">
          Форма смены пароля
        </h2>

        <div className="space-y-4">
          <PasswordField
            id="current-password"
            label={forced ? "Пароль, выданный администратором" : "Текущий пароль"}
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            disabled={submitting}
            autoFocus
          />
          <PasswordField
            id="new-password"
            label="Новый пароль"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            disabled={submitting}
            hint="Не короче 12 символов, минимум одна буква и одна цифра; логин внутри пароля недопустим."
          />
          <PasswordField
            id="confirm-password"
            label="Новый пароль ещё раз"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            disabled={submitting}
          />
        </div>

        {error && (
          <p
            role="alert"
            aria-live="assertive"
            className="mt-4 rounded-[var(--radius-m)] bg-overdue-bg px-3 py-2 text-[13px] text-overdue-fg"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-[var(--radius-m)] bg-accent px-4 py-2 text-[15px] font-bold text-surface disabled:opacity-60"
          >
            {submitting ? "Сохранение…" : "Сменить пароль"}
          </button>
          {forced ? (
            // В принудительном режиме единственный выход, кроме смены пароля, — выйти из
            // системы. Кнопка есть именно поэтому: иначе человек, который сейчас менять
            // пароль не готов, остался бы на экране без единого действия.
            <button
              type="button"
              onClick={() => void logout()}
              disabled={submitting}
              className="rounded-[var(--radius-m)] border border-border px-4 py-2 text-[15px] text-ink disabled:opacity-60"
            >
              Выйти
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void navigate(-1)}
              disabled={submitting}
              className="rounded-[var(--radius-m)] border border-border px-4 py-2 text-[15px] text-ink disabled:opacity-60"
            >
              Отмена
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
